import { createStore } from "devextreme-aspnet-data-nojquery";
import { urlEncodedContentType, jsonContentType, fetchData } from './fetch-data';


const {
  staffListApiConfig: { url },
  defaultScopes: scopes,
  timeZone
} = window.env;

export default class StaffListApiService {
  getAuthHeaderAsync = async () => { };
  constructor({ getAuthorizationHeaderAsync }) {
    if (!getAuthorizationHeaderAsync)
      throw Error(
        "An authorization service must be specified for the protected graph API."
      );
    this.getAuthHeaderAsync = getAuthorizationHeaderAsync;
    this.getSchoolsForWhichUserIsAStaffListManagerAsync = this.getSchoolsForWhichUserIsAStaffListManagerAsync.bind(this);
    this.getStaffListBySchoolAsync = this.getStaffListBySchoolAsync.bind(this);
    this.requestRecordReviewAsync = this.requestRecordReviewAsync.bind(this);
    this.requestSchoolAssociationRemovalAsync = this.requestSchoolAssociationRemovalAsync.bind(this);
    this.updateStaffListCurrentDateAsync = this.updateStaffListCurrentDateAsync.bind(this);
    this.findMemberByNameAsync = this.findMemberByNameAsync.bind(this);
    this.requestSchoolAssociationAdditionAsync = this.requestSchoolAssociationAdditionAsync.bind(this);
    this.getCurrentSchoolYearInfo = this.getCurrentSchoolYearInfo.bind(this);
    this.sendEmailToChangeCSRRole = this.sendEmailToChangeCSRRole.bind(this);
    this.updateMemberEmploymentStatus = this.updateMemberEmploymentStatus.bind(this);
    this.getCommitteesForWhichUserIsMemberAsync = this.getCommitteesForWhichUserIsMemberAsync.bind(this);
    this.getCommitteeSummaryAsync = this.getCommitteeSummaryAsync.bind(this);
    this.listCommitteeDocumentsAsync = this.listCommitteeDocumentsAsync.bind(this);
    this.downloadCommitteeDocumentAsync = this.downloadCommitteeDocumentAsync.bind(this);
  }

  // Lists the immediate children (files and sub-folders) of a committee's
  // document folder. `path` is the folder within the committee (empty = root).
  async listCommitteeDocumentsAsync(committeeId, path) {
    const relative = path ? `/${path.replace(/^\/+/, '')}` : '';
    const prefix = `committees/${committeeId}${relative}`;
    const fetchUrl = `${url}/ListAttachments?${new URLSearchParams({ committeeId, prefix })}`;
    return await fetchData(fetchUrl, this.getAuthHeaderAsync, scopes, 'GET', urlEncodedContentType);
  }

  // Downloads a single document (read-only) and returns it as a Blob.
  // `fileName` is the full blob path (e.g. "committees/3/sub/report.pdf").
  async downloadCommitteeDocumentAsync(committeeId, fileName) {
    const fetchUrl = `${url}/DownloadAttachment?${new URLSearchParams({ committeeId, fileName })}`;
    const authHeaderValue = await this.getAuthHeaderAsync(scopes);
    const response = await fetch(fetchUrl, {
      method: 'GET',
      headers: new Headers({ Authorization: authHeaderValue }),
    });
    if (!response.ok) {
      // Surface the backend message. The body is a JSON-encoded string whether it
      // comes from a StatusCode(...) result or the error-handler middleware.
      let message = await response.text();
      try {
        message = JSON.parse(message);
      } catch {
        // body was plain text, not a JSON-encoded string — use it as-is.
      }
      throw new Error(message || `Download failed (${response.status})`);
    }
    return await response.blob();
  }

  async getSchoolsForWhichUserIsAStaffListManagerAsync() {
    const params = {
      timeZone: window.env.timeZone,
    };
    const fetchUrl = `${url}/GetSchoolsForWhichUserIsAStaffListManager?${new URLSearchParams(params)}`;
    const response = await fetchData(fetchUrl, this.getAuthHeaderAsync, scopes, 'GET', urlEncodedContentType);
    return response;
  }

  async getCommitteesForWhichUserIsMemberAsync() {
    const params = {
      timeZone: window.env.timeZone,
    };
    const fetchUrl = `${url}/GetCommitteesForWhichUserIsMember?${new URLSearchParams(params)}`;
    const response = await fetchData(fetchUrl, this.getAuthHeaderAsync, scopes, 'GET', urlEncodedContentType);
    return response;
  }

  // { dateJoined, memberCount, documentCount } for the logon user's committee card.
  async getCommitteeSummaryAsync(committeeId) {
    const fetchUrl = `${url}/GetCommitteeSummary?${new URLSearchParams({ committeeId })}`;
    return await fetchData(fetchUrl, this.getAuthHeaderAsync, scopes, 'GET', urlEncodedContentType);
  }

  async getStaffListBySchoolAsync(schoolId) {
    const params = {
      schoolId,
      timeZone 
    };
    const fetchUrl = `${url}/GetStaffListBySchool?${new URLSearchParams(params)}`;
    return await fetchData(fetchUrl, this.getAuthHeaderAsync, scopes, 'GET', urlEncodedContentType);
  }

  async getCurrentSchoolYearInfo() {
    const fetchUrl = `${url}/GetCurrentSchoolYearInfo`;
    return await fetchData(fetchUrl, this.getAuthHeaderAsync, scopes, 'GET', urlEncodedContentType);
  }

  findMemberByNameAsync() {
    return createStore({
      key: "Id",
      loadUrl: `${url}/FindMemberByName`,
      onBeforeSend: async (_, ajaxOptions) => {
        const authHeaderValue = await this.getAuthHeaderAsync(scopes);
        if (authHeaderValue) {
          if (!ajaxOptions.headers) {
            ajaxOptions.headers = {};
          }
          Object.assign(ajaxOptions.headers, {
            Authorization: authHeaderValue,
          });
        }
      },
    });
  }

  async requestRecordReviewAsync(schoolId, memberIds, message) {
    const body = JSON.stringify({ 'SchoolId': schoolId, 'MemberIds': memberIds, 'Message': message });
    const fetchUrl = `${url}/RequestRecordReview`
    const response = await fetchData(fetchUrl, this.getAuthHeaderAsync, scopes, 'POST', jsonContentType, body);
    return response;
  }

  async requestSchoolAssociationRemovalAsync(schoolId, memberIds, message) {
    const body = JSON.stringify({ 'SchoolId': schoolId, 'MemberSchoolInfo': memberIds, 'Message': message });
    const fetchUrl = `${url}/RequestSchoolAssociationRemoval`
    const response = await fetchData(fetchUrl, this.getAuthHeaderAsync, scopes, 'POST', jsonContentType, body);
    return response;
  }

  async updateStaffListCurrentDateAsync(schoolId) {
    const params = {
      schoolId: schoolId,
    };
    const fetchUrl = `${url}/UpdateStaffListCurrentDate?${new URLSearchParams(params)}`;
    const response = await fetchData(fetchUrl, this.getAuthHeaderAsync, scopes, 'POST', urlEncodedContentType);
    return response;
  }

  async requestSchoolAssociationAdditionAsync(schoolId, memberIds, emailAddresses) {
    const body = JSON.stringify({ 'SchoolId': schoolId, 'MemberSchoolInfo': memberIds, 'EmailAddresses': emailAddresses });
    const fetchUrl = `${url}/RequestSchoolAssociationAddition`;
    const response = await fetchData(fetchUrl, this.getAuthHeaderAsync, scopes, 'POST', jsonContentType, body);
    return response;
  }

  async sendEmailToChangeCSRRole(msg, id) {
    const body = JSON.stringify({ 'Message': msg, 'Id': id });
    const fetchUrl = `${url}/SendEmailToChangeCSRRole`
    const response = await fetchData(fetchUrl, this.getAuthHeaderAsync, scopes, 'POST', jsonContentType, body);
    return response;
  }

  async updateMemberEmploymentStatus(schoolId, memberId, employmentStatusId, effectiveDate) {
    const params = {
      schoolId: schoolId,
    };

    const body = JSON.stringify({ MemberId: memberId, EmploymentStatusId: employmentStatusId, effectiveDate: effectiveDate });
    const fetchUrl = `${url}/UpdateMemberEmploymentStatus?${new URLSearchParams(params)}`;
    const response = await fetchData(fetchUrl, this.getAuthHeaderAsync, scopes, 'POST', jsonContentType, body);
    return response;
  }
}